"use client";

/**
 * A developer's logo tile, or a monogram when there is none.
 *
 * The logo is OUR processed WebP (backend services/developer_logos): the
 * logo centred on its own light or dark tile, baked into the image, so it
 * reads the same in both themes and is never recoloured here. The
 * monogram uses the gray tokens, which flip with the theme.
 *
 * A tile that fails to load always falls back to the monogram, never a
 * broken image (owner, 2026-10-21), including one that failed before
 * React hydrated, whose error event nobody heard: the mount effect looks
 * at the finished <img> itself.
 */
import { useEffect, useRef, useState } from "react";
import { mediaSrc, type DeveloperLogoRef } from "@/lib/api";

const LEGAL = new Set(["mmc", "mtk", "llc", "ltd", "group", "qrup", "co", "company"]);

export function monogram(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((w) => w && !LEGAL.has(w.toLocaleLowerCase("az")));
  const pick = (words.length ? words : name.split(/\s+/)).slice(0, 2);
  return pick.map((w) => w[0]).join("").toLocaleUpperCase("az") || "?";
}

/** A wordmark gets a 2:1 tile; it is never squeezed into a square. */
const WIDE_ASPECT = 1.6;
/** Below this height a wordmark in a square tile is unreadable. */
const MIN_SQUARE_WORDMARK = 32;

export default function DeveloperLogo({
  name,
  logo,
  size = 32,
  allowWide = true,
  className = "",
}: {
  name: string;
  logo?: DeveloperLogoRef | null;
  /** Tile height in css px; square logos are size x size, wordmarks
   *  2*size x size. */
  size?: number;
  /** False where the layout only has a square slot: a wordmark then
   *  falls back to its monogram below 32 px. */
  allowWide?: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const img = useRef<HTMLImageElement>(null);
  const radius = size <= 24 ? "rounded" : "rounded-lg";
  const wordmark = !!logo && (logo.aspect ?? 0) >= WIDE_ASPECT;
  let src: string | undefined;
  let width = size;
  if (logo && !failed) {
    if (wordmark && allowWide && logo.wide) {
      width = size * 2;
      src = mediaSrc(size > 24 ? logo.wide : logo.wide_sm || logo.wide);
    } else if (!wordmark || size >= MIN_SQUARE_WORDMARK) {
      src = mediaSrc(size <= 32 ? logo.sm : logo.lg);
    }
  }
  // A server-rendered tile that finished with no size before React
  // listened failed: its error event is gone, so look at the <img>.
  useEffect(() => {
    const el = img.current;
    if (src && el && el.complete && el.naturalWidth === 0) setFailed(true);
  }, [src]);
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        ref={img}
        src={src}
        alt=""
        width={width}
        height={size}
        loading="lazy"
        onError={() => setFailed(true)}
        className={`shrink-0 ${radius} ring-1 ring-gray-200 ${className}`}
        style={{ width, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center ${radius} bg-gray-100 font-semibold text-gray-600 ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.38)) }}
    >
      {monogram(name)}
    </span>
  );
}
